import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi, type SegredoDoSegundoFator } from '../api/auth.js';
import { mensagemDoErro } from '../api/cliente.js';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Botao, Campo, Formulario, Status } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';

/**
 * A ativação do segundo fator pela PRÓPRIA pessoa — a pendência do
 * documento 02: "mostrar segredo/URI otpauth e códigos de recuperação
 * uma vez, confirmar com código".
 *
 * Sem biblioteca de QR: o protótipo não pede leitor de câmera, e o
 * segredo em Base32 mais a URL `otpauth://` bastam para cadastrar num
 * app autenticador manualmente. Acrescentar geração de QR é trabalho
 * de imagem sem ganho de comportamento — fica registrado no relatório
 * como possível melhoria, não como pendência.
 *
 * Duas fases, como o backend exige: gerar o segredo NÃO liga a
 * exigência; só confirmar com um código de 6 dígitos liga.
 */
export function ModalDeSegundoFator({ onFechar }: { onFechar: () => void }) {
  const cliente = useQueryClient();
  const avisar = useToast();
  const [gerado, setGerado] = useState<SegredoDoSegundoFator | null>(null);
  const [codigo, setCodigo] = useState('');
  const [senha, setSenha] = useState('');
  const [codigoParaDesativar, setCodigoParaDesativar] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const situacao = useQuery({ queryKey: ['auth', '2fa'], queryFn: authApi.situacaoTotp });

  function invalidar() {
    void cliente.invalidateQueries({ queryKey: ['auth', '2fa'] });
    void cliente.invalidateQueries({ queryKey: ['acessos', 'seguranca'] });
  }

  const iniciar = useMutation({
    mutationFn: () => authApi.iniciarTotp(),
    onSuccess: setGerado,
    onError: (e) => setErro(mensagemDoErro(e)),
  });

  const ativar = useMutation({
    mutationFn: () => authApi.ativarTotp(codigo.trim()),
    onSuccess: () => {
      invalidar();
      avisar('Segundo fator ativado.');
      onFechar();
    },
    onError: (e) => setErro(mensagemDoErro(e)),
  });

  const desativar = useMutation({
    mutationFn: () => authApi.desativarTotp(senha, codigoParaDesativar.trim()),
    onSuccess: () => {
      invalidar();
      avisar('Segundo fator desativado. As sessões abertas foram encerradas.', 'aviso');
      onFechar();
    },
    onError: (e) => setErro(mensagemDoErro(e)),
  });

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo="Segundo fator"
      descricao="Opcional, por aplicativo autenticador."
    >
      <CorpoDeModal>
        {erro && (
          <div
            className="empty"
            role="alert"
            style={{ borderColor: 'var(--red)', marginBottom: 14 }}
          >
            {erro}
          </div>
        )}

        {situacao.isPending && <p className="sub">Carregando…</p>}

        {situacao.data?.ativo && (
          <>
            <p className="sub">
              <Status situacao="ok">Ativo</Status> · {situacao.data.codigosRestantes} códigos de
              recuperação restantes.
            </p>
            <Formulario onSubmit={(e) => e.preventDefault()}>
              <Campo rotulo="Senha">
                <input
                  type="password"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  autoComplete="current-password"
                />
              </Campo>
              <Campo rotulo="Código do app (ou de recuperação)">
                <input
                  value={codigoParaDesativar}
                  onChange={(e) => setCodigoParaDesativar(e.target.value)}
                  placeholder="000000"
                />
              </Campo>
              <Botao
                variante="contorno"
                disabled={!senha || !codigoParaDesativar || desativar.isPending}
                onClick={() => {
                  setErro(null);
                  desativar.mutate();
                }}
              >
                Desativar segundo fator
              </Botao>
            </Formulario>
          </>
        )}

        {situacao.data && !situacao.data.ativo && !gerado && (
          <>
            <p className="sub">
              Ainda não ativado. Gerar o segredo mostra o código e os avisos de recuperação uma
              única vez.
            </p>
            <Botao onClick={() => iniciar.mutate()} disabled={iniciar.isPending}>
              Gerar segredo
            </Botao>
          </>
        )}

        {gerado && (
          <div style={{ display: 'grid', gap: 14 }}>
            <div>
              <label className="sub" style={{ display: 'block', marginBottom: 6 }}>
                Segredo (digite no app, se não conseguir ler o link)
              </label>
              <code style={{ userSelect: 'all', wordBreak: 'break-all' }}>{gerado.segredo}</code>
            </div>
            <div>
              <label className="sub" style={{ display: 'block', marginBottom: 6 }}>
                Link para o app autenticador
              </label>
              <code style={{ userSelect: 'all', wordBreak: 'break-all', fontSize: 11.5 }}>
                {gerado.url}
              </code>
            </div>
            <div>
              <label className="sub" style={{ display: 'block', marginBottom: 6 }}>
                Códigos de recuperação — guarde-os agora, eles não aparecem de novo
              </label>
              <ul
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 6,
                  margin: 0,
                  padding: 0,
                  listStyle: 'none',
                }}
              >
                {gerado.recuperacao.map((c) => (
                  <li key={c}>
                    <code>{c}</code>
                  </li>
                ))}
              </ul>
            </div>
            <Campo rotulo="Código do app, para confirmar">
              <input
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                placeholder="000000"
                autoFocus
              />
            </Campo>
            <Botao
              disabled={codigo.trim().length < 6 || ativar.isPending}
              onClick={() => {
                setErro(null);
                ativar.mutate();
              }}
            >
              Confirmar e ativar
            </Botao>
          </div>
        )}
      </CorpoDeModal>
    </Modal>
  );
}
